import { createSlice } from '@reduxjs/toolkit';
import {
  DEFAULT_NOTIFICATION_BY_TAB,
  mergeNotificationsByTab,
  markAllNotificationsAsRead,
} from '@native-components/notification-center/utils';
import {
  getNotifications,
  loadMoreNotifications,
  getUnreadNotificationsCount,
  markAllNotificationsAsSeen,
  markNotificationAsSeen,
  fetchNotificationPreferences,
  updateNotificationPreference,
  getNotificationsByTab,
  loadMoreNotificationsByTab,
} from './thunks';
import { NOTIFICATION_PAGE_LIMIT } from './constants';

const DEFAULT_LIMIT = NOTIFICATION_PAGE_LIMIT;
const DEFAULT_VISIBILITY_BY_TAB = {
  order: true,
  loyalty: true,
  info: false,
};
const DEFAULT_TAB_KEYS = ['order', 'loyalty', 'info'];
const getDefaultSkipByTab = () => ({ order: 0, loyalty: 0, info: 0 });
const getDefaultHasMoreByTab = () => ({
  order: true,
  loyalty: true,
  info: true,
});
const getDefaultStatusByTab = (value) => ({
  order: value,
  loyalty: value,
  info: value,
});

const pickPhase1Tabs = (notificationsByTab = {}) => ({
  order: [...(notificationsByTab.order || [])],
  loyalty: [...(notificationsByTab.loyalty || [])],
});

const getDefaultNotificationsByTab = () => ({
  ...pickPhase1Tabs(DEFAULT_NOTIFICATION_BY_TAB),
});

const getDefaultUnreadCountByTab = () => ({ order: 0, loyalty: 0, info: 0 });
const getDefaultVisibilityDisabledAtByTab = () => ({
  order: null,
  loyalty: null,
  info: null,
});
const getDefaultFrozenUnreadCountByTab = () => ({
  order: null,
  loyalty: null,
  info: null,
});

// Tracks when a tab was last turned off (and the unread count at that
// moment) so newly-arriving notifications/unread counts can stay hidden
// while the tab is disabled, without touching notifications that already
// existed before it was turned off.
const applyVisibilityChange = (state, key, nextValue) => {
  if (!Object.prototype.hasOwnProperty.call(DEFAULT_VISIBILITY_BY_TAB, key)) {
    return;
  }

  const wasVisible = state.visibilityByTab?.[key] ?? true;
  state.visibilityByTab = {
    ...state.visibilityByTab,
    [key]: nextValue,
  };

  if (nextValue) {
    state.visibilityDisabledAtByTab = {
      ...state.visibilityDisabledAtByTab,
      [key]: null,
    };
    state.frozenUnreadCountByTab = {
      ...state.frozenUnreadCountByTab,
      [key]: null,
    };
    return;
  }

  if (wasVisible) {
    state.visibilityDisabledAtByTab = {
      ...state.visibilityDisabledAtByTab,
      [key]: Date.now(),
    };
    state.frozenUnreadCountByTab = {
      ...state.frozenUnreadCountByTab,
      [key]: state.unreadCountByTab?.[key] ?? 0,
    };
  }
};

export const { actions, reducer } = createSlice({
  name: 'notificationCenter',
  initialState: {
    statusApiNotifications: 'idle',
    statusApiLoadMore: 'succeeded',
    statusApiUnreadCount: 'idle',
    statusApiMarkAllRead: 'idle',
    statusApiPreferences: 'idle',
    notificationsByTab: getDefaultNotificationsByTab(),
    unreadCount: 0,
    unreadCountByTab: getDefaultUnreadCountByTab(),
    unreadCountFetchedAt: 0,
    visibilityByTab: DEFAULT_VISIBILITY_BY_TAB,
    visibilityDisabledAtByTab: getDefaultVisibilityDisabledAtByTab(),
    frozenUnreadCountByTab: getDefaultFrozenUnreadCountByTab(),
    skip: 0,
    limit: DEFAULT_LIMIT,
    hasMore: true,
    // Per-tab state (used when fetching per-tab detail screen)
    skipByTab: getDefaultSkipByTab(),
    hasMoreByTab: getDefaultHasMoreByTab(),
    statusApiNotificationsByTab: getDefaultStatusByTab('idle'),
    statusApiLoadMoreByTab: getDefaultStatusByTab('succeeded'),
  },
  reducers: {
    resetNotificationsState(state) {
      state.statusApiNotifications = 'idle';
      state.statusApiLoadMore = 'succeeded';
      state.statusApiUnreadCount = 'idle';
      state.statusApiMarkAllRead = 'idle';
      state.notificationsByTab = getDefaultNotificationsByTab();
      state.unreadCount = 0;
      state.unreadCountByTab = getDefaultUnreadCountByTab();
      state.unreadCountFetchedAt = 0;
      state.visibilityByTab = DEFAULT_VISIBILITY_BY_TAB;
      state.visibilityDisabledAtByTab = getDefaultVisibilityDisabledAtByTab();
      state.frozenUnreadCountByTab = getDefaultFrozenUnreadCountByTab();
      state.statusApiPreferences = 'idle';
      state.skip = 0;
      state.limit = DEFAULT_LIMIT;
      state.hasMore = true;
      state.skipByTab = getDefaultSkipByTab();
      state.hasMoreByTab = getDefaultHasMoreByTab();
      state.statusApiNotificationsByTab = getDefaultStatusByTab('idle');
      state.statusApiLoadMoreByTab = getDefaultStatusByTab('succeeded');
    },
    setNotificationTabVisibility(state, { payload }) {
      const key = payload?.key;
      const value = payload?.value;

      if (typeof value !== 'boolean') {
        return;
      }

      applyVisibilityChange(state, key, value);
    },
    hydrateVisibilityMeta(state, { payload }) {
      if (!payload || typeof payload !== 'object') {
        return;
      }

      const {
        visibilityByTab,
        visibilityDisabledAtByTab,
        frozenUnreadCountByTab,
      } = payload;

      if (visibilityByTab && typeof visibilityByTab === 'object') {
        const next = { ...state.visibilityByTab };
        for (const key of DEFAULT_TAB_KEYS) {
          if (typeof visibilityByTab[key] === 'boolean') {
            next[key] = visibilityByTab[key];
          }
        }
        state.visibilityByTab = next;
      }

      if (
        visibilityDisabledAtByTab &&
        typeof visibilityDisabledAtByTab === 'object'
      ) {
        const next = { ...state.visibilityDisabledAtByTab };
        for (const key of DEFAULT_TAB_KEYS) {
          const value = visibilityDisabledAtByTab[key];
          if (typeof value === 'number' || value === null) {
            next[key] = value;
          }
        }
        state.visibilityDisabledAtByTab = next;
      }

      if (
        frozenUnreadCountByTab &&
        typeof frozenUnreadCountByTab === 'object'
      ) {
        const next = { ...state.frozenUnreadCountByTab };
        for (const key of DEFAULT_TAB_KEYS) {
          const value = frozenUnreadCountByTab[key];
          if (typeof value === 'number' || value === null) {
            next[key] = value;
          }
        }
        state.frozenUnreadCountByTab = next;
      }
    },
    markAllAsRead(state) {
      state.notificationsByTab = markAllNotificationsAsRead(
        state.notificationsByTab,
      );
      state.unreadCount = 0;
      state.unreadCountByTab = getDefaultUnreadCountByTab();
    },
    markNotificationAsRead(state, { payload: { id } }) {
      for (const tabKey of Object.keys(state.notificationsByTab)) {
        const list = state.notificationsByTab[tabKey];
        const index = list.findIndex((n) => n.id === id);
        if (index !== -1 && list[index].unread) {
          list[index] = { ...list[index], unread: false };
          state.unreadCount = Math.max(0, state.unreadCount - 1);
          if (tabKey in state.unreadCountByTab) {
            state.unreadCountByTab[tabKey] = Math.max(
              0,
              state.unreadCountByTab[tabKey] - 1,
            );
          }
          break;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getNotifications.pending, (state) => {
        state.statusApiNotifications = 'pending';
      })
      .addCase(getNotifications.rejected, (state) => {
        state.statusApiNotifications = 'failed';
        state.statusApiLoadMore = 'failed';
        state.notificationsByTab = getDefaultNotificationsByTab();
        state.skip = 0;
        state.hasMore = false;
      })
      .addCase(getNotifications.fulfilled, (state, { payload }) => {
        state.statusApiNotifications = 'succeeded';
        state.statusApiLoadMore = 'succeeded';
        state.notificationsByTab = pickPhase1Tabs(payload.notificationsByTab);
        state.skip = payload.nextSkip;
        state.limit = payload.limit;
        state.hasMore = payload.fetchedCount > 0 ? payload.hasMore : false;
      })
      .addCase(loadMoreNotifications.pending, (state) => {
        state.statusApiLoadMore = 'loading';
      })
      .addCase(loadMoreNotifications.rejected, (state) => {
        state.statusApiLoadMore = 'failed';
      })
      .addCase(loadMoreNotifications.fulfilled, (state, { payload }) => {
        const { notificationsByTab, uniqueIncomingCount } =
          mergeNotificationsByTab(
            state.notificationsByTab,
            payload.notificationsByTab,
          );

        state.statusApiLoadMore = 'succeeded';
        state.notificationsByTab = pickPhase1Tabs(notificationsByTab);
        state.skip = payload.nextSkip;
        state.limit = payload.limit;
        state.hasMore =
          payload.hasMore &&
          payload.fetchedCount > 0 &&
          uniqueIncomingCount > 0;
      })
      .addCase(getUnreadNotificationsCount.pending, (state) => {
        state.statusApiUnreadCount = 'pending';
      })
      .addCase(getUnreadNotificationsCount.rejected, (state) => {
        state.statusApiUnreadCount = 'failed';
      })
      .addCase(getUnreadNotificationsCount.fulfilled, (state, { payload }) => {
        state.statusApiUnreadCount = 'succeeded';
        state.unreadCount = payload.total;
        state.unreadCountByTab = payload.byTab;
        state.unreadCountFetchedAt = Date.now();
      })
      .addCase(markAllNotificationsAsSeen.pending, (state) => {
        state.statusApiMarkAllRead = 'pending';
      })
      .addCase(markAllNotificationsAsSeen.rejected, (state) => {
        state.statusApiMarkAllRead = 'failed';
      })
      .addCase(markAllNotificationsAsSeen.fulfilled, (state) => {
        state.statusApiMarkAllRead = 'succeeded';
        state.notificationsByTab = markAllNotificationsAsRead(
          state.notificationsByTab,
        );
        state.unreadCount = 0;
        state.unreadCountByTab = getDefaultUnreadCountByTab();
        state.unreadCountFetchedAt = Date.now();
      })
      .addCase(markNotificationAsSeen.rejected, (state, { meta }) => {
        // Rollback optimistic update on failure
        const id = meta?.arg;
        if (!id) {
          return;
        }
        for (const tabKey of Object.keys(state.notificationsByTab)) {
          const list = state.notificationsByTab[tabKey];
          const index = list.findIndex((n) => n.id === id);
          if (index !== -1 && !list[index].unread) {
            list[index] = { ...list[index], unread: true };
            state.unreadCount += 1;
            if (tabKey in state.unreadCountByTab) {
              state.unreadCountByTab[tabKey] += 1;
            }
            break;
          }
        }
      })
      .addCase(fetchNotificationPreferences.pending, (state) => {
        state.statusApiPreferences = 'pending';
      })
      .addCase(fetchNotificationPreferences.rejected, (state) => {
        state.statusApiPreferences = 'failed';
      })
      .addCase(fetchNotificationPreferences.fulfilled, (state, { payload }) => {
        state.statusApiPreferences = 'succeeded';
        // Merge only the server-backed keys; loyalty stays untouched (local only)
        for (const [key, value] of Object.entries(payload || {})) {
          if (typeof value === 'boolean') {
            applyVisibilityChange(state, key, value);
          }
        }
      })
      .addCase(updateNotificationPreference.rejected, (state, { meta }) => {
        // Roll back the optimistic toggle on failure
        const key = meta?.arg?.key;
        const previousValue = meta?.arg?.previousValue;
        if (key && typeof previousValue === 'boolean') {
          applyVisibilityChange(state, key, previousValue);
        }
      })

      // Per-tab fetch (detail screen)
      .addCase(getNotificationsByTab.pending, (state, { meta }) => {
        const tab = meta?.arg?.tab;
        if (tab && DEFAULT_TAB_KEYS.includes(tab)) {
          state.statusApiNotificationsByTab[tab] = 'pending';
        }
      })
      .addCase(getNotificationsByTab.rejected, (state, { meta }) => {
        const tab = meta?.arg?.tab;
        if (tab && DEFAULT_TAB_KEYS.includes(tab)) {
          state.statusApiNotificationsByTab[tab] = 'failed';
          state.statusApiLoadMoreByTab[tab] = 'failed';
          state.notificationsByTab[tab] = [];
          state.skipByTab[tab] = 0;
          state.hasMoreByTab[tab] = false;
        }
      })
      .addCase(getNotificationsByTab.fulfilled, (state, { payload }) => {
        const { tab, notificationsByTab, nextSkip, hasMore, fetchedCount } =
          payload;
        if (!tab || !DEFAULT_TAB_KEYS.includes(tab)) {
          return;
        }
        // Flatten all mapped items into target tab (handles edge-case misrouting)
        const allItems = Object.values(notificationsByTab).flat();
        // Preserve items already in state that the per-tab API didn't return
        // (e.g. items with different notification_category loaded via the "all" tab fetch)
        const existingItems = state.notificationsByTab[tab] || [];
        const newItemIds = new Set(allItems.map((item) => item.id));
        const itemsToPreserve = existingItems.filter(
          (item) => !newItemIds.has(item.id),
        );
        const merged = [...allItems, ...itemsToPreserve].sort((a, b) => {
          const aTs = new Date(a.createdAt || 0).getTime();
          const bTs = new Date(b.createdAt || 0).getTime();
          return bTs - aTs;
        });
        state.statusApiNotificationsByTab[tab] = 'succeeded';
        state.statusApiLoadMoreByTab[tab] = 'succeeded';
        state.notificationsByTab[tab] = merged;
        state.skipByTab[tab] = nextSkip;
        state.hasMoreByTab[tab] = fetchedCount > 0 ? hasMore : false;
      })

      // Per-tab load more (detail screen pagination)
      .addCase(loadMoreNotificationsByTab.pending, (state, { meta }) => {
        const tab = meta?.arg?.tab;
        if (tab && DEFAULT_TAB_KEYS.includes(tab)) {
          state.statusApiLoadMoreByTab[tab] = 'loading';
        }
      })
      .addCase(loadMoreNotificationsByTab.rejected, (state, { meta }) => {
        const tab = meta?.arg?.tab;
        if (tab && DEFAULT_TAB_KEYS.includes(tab)) {
          state.statusApiLoadMoreByTab[tab] = 'failed';
        }
      })
      .addCase(loadMoreNotificationsByTab.fulfilled, (state, { payload }) => {
        const {
          tab,
          notificationsByTab: incomingByTab,
          nextSkip,
          hasMore,
          fetchedCount,
        } = payload;
        if (!tab || !DEFAULT_TAB_KEYS.includes(tab)) {
          return;
        }
        // Deduplicate incoming items against current tab list
        const existingIds = new Set(
          (state.notificationsByTab[tab] || []).map((n) => n.id),
        );
        const incomingItems = Object.values(incomingByTab).flat();
        const uniqueItems = incomingItems.filter((n) => !existingIds.has(n.id));

        state.statusApiLoadMoreByTab[tab] = 'succeeded';
        state.notificationsByTab[tab] = [
          ...(state.notificationsByTab[tab] || []),
          ...uniqueItems,
        ];
        state.skipByTab[tab] = nextSkip;
        state.hasMoreByTab[tab] =
          hasMore && fetchedCount > 0 && uniqueItems.length > 0;
      });
  },
});

export default reducer;
